import axios from 'axios';

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://yabatho-api.onrender.com/v2/api';
const AUTH_URL = `${BASE_URL}/auth`;

const defaultHeaders = {
  'Content-Type': 'application/json',
};

const getStoredToken = () => {
  return localStorage.getItem('token');
};

const api = axios.create({
  baseURL: BASE_URL,
  headers: defaultHeaders,
});

api.interceptors.request.use(
  (config) => {
    const token = getStoredToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ===== AUTH: REGISTER CUSTOMER =====
export const signUp = async ({
  email,
  password,
  firstName,
  lastName,
  phone = '',
}) => {
  try {
    const res = await axios.post(
      `${AUTH_URL}/register`,
      {
        email,
        password,
        firstName,
        lastName,
        name: `${firstName} ${lastName}`.trim(),
        phone,
        role: 'customer',
      },
      { headers: defaultHeaders },
    );

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Signup failed');
  }
};

// Alias for older pages that import register/signup differently
export const register = signUp;

// ===== AUTH: LOGIN CUSTOMER =====
export const login = async ({ email, password }) => {
  try {
    const res = await axios.post(
      `${AUTH_URL}/login`,
      {
        email,
        password,
        role: 'customer',
      },
      { headers: defaultHeaders },
    );

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Login failed');
  }
};

// ===== AUTH: GOOGLE LOGIN CUSTOMER =====
export const googleLogin = async ({ idToken }) => {
  try {
    const res = await axios.post(
      `${AUTH_URL}/google`,
      { idToken },
      { headers: defaultHeaders },
    );
    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Google login failed');
  }
};

// ===== AUTH: REQUEST PASSWORD RESET OTP =====
export const requestPasswordReset = async ({ email }) => {
  try {
    const res = await axios.post(
      `${AUTH_URL}/request-password-reset`,
      { email },
      { headers: defaultHeaders },
    );

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Request password reset failed');
  }
};

// ===== AUTH: VERIFY OTP =====
export const verifyOtp = async ({ email, otp }) => {
  try {
    const res = await axios.post(
      `${AUTH_URL}/verify-otp`,
      {
        email,
        otp,
      },
      { headers: defaultHeaders },
    );

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'OTP verification failed');
  }
};

// Alias for pages using different casing
export const verifyOTP = verifyOtp;

// ===== AUTH: RESET PASSWORD =====
export const resetPassword = async ({
  email,
  otp,
  newPassword,
  newPassword_confirmation,
}) => {
  try {
    const res = await axios.post(
      `${AUTH_URL}/reset-password`,
      {
        email,
        otp,
        newPassword,
        newPassword_confirmation,
      },
      { headers: defaultHeaders },
    );

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Password reset failed');
  }
};

// ===== AUTH: CHANGE PASSWORD =====
export const changePassword = async ({
  currentPassword,
  newPassword,
  newPassword_confirmation,
}) => {
  try {
    const res = await api.put('/users/change-password', {
      currentPassword,
      newPassword,
      newPassword_confirmation,
    });

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to change password');
  }
};

// ===== USER PROFILE =====
export const getProfile = async () => {
  try {
    const res = await api.get('/users/me');

    return res.data?.user || res.data?.data?.user || res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to get profile');
  }
};

export const updateProfile = async (profileData) => {
  try {
    const res = await api.put('/users/me', profileData);

    return res.data?.user || res.data?.data?.user || res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to update profile');
  }
};

// ===== RESTAURANTS / OUTLETS =====
export const getRestaurants = async (params = {}) => {
  try {
    const res = await api.get('/restaurants', {
      params: {
        page: params.page || 1,
        limit: params.limit || 50,
        isAvailable: params.isAvailable,
        ...params,
      },
    });

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to fetch restaurants');
  }
};

export const getRestaurantById = async (restaurantId) => {
  try {
    const res = await api.get(`/restaurants/${restaurantId}`);

    return res.data?.data || res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to fetch restaurant');
  }
};

export const getMenuItems = async (restaurantId) => {
  try {
    const res = await api.get(`/restaurants/${restaurantId}/menu-items`);

    return res.data?.data || res.data || [];
  } catch (error) {
    handleAxiosError(error, 'Failed to fetch menu items');
  }
};

export const searchRestaurants = async (q) => {
  try {
    const res = await api.get('/restaurants/search', {
      params: { q },
    });

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to search restaurants');
  }
};

export const getNearbyRestaurants = async (lat, lng, radius = 5) => {
  try {
    const res = await api.get('/restaurants/nearby', {
      params: {
        latitude: lat,
        longitude: lng,
        radius,
      },
    });

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to fetch nearby restaurants');
  }
};

export const getCuisineTypes = async () => {
  try {
    const res = await api.get('/restaurants/cuisine-types');

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to fetch cuisine types');
  }
};

export const getTopRated = async () => {
  try {
    const res = await api.get('/restaurants/top-rated');

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to fetch top-rated restaurants');
  }
};

export const getPopularRestaurants = async () => {
  try {
    const res = await api.get('/restaurants/popular');

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to fetch popular restaurants');
  }
};

// ===== ORDERS =====
export const placeOrder = async (orderData) => {
  try {
    const res = await api.post('/orders', orderData);

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to place order');
  }
};

export const getOrders = async () => {
  try {
    const firstResponse = await api.get('/orders', {params: {page: 1, limit: 50}});
    const firstPage = Array.isArray(firstResponse.data?.data)
      ? firstResponse.data.data
      : Array.isArray(firstResponse.data)
        ? firstResponse.data
        : [];
    const lastPage = Math.max(1, Number(firstResponse.data?.meta?.lastPage || 1));

    if (lastPage === 1) return firstPage;

    const remainingPages = await Promise.all(
      Array.from({length: lastPage - 1}, (_, index) =>
        api.get('/orders', {params: {page: index + 2, limit: 50}}),
      ),
    );

    return remainingPages.reduce((orders, response) => {
      const pageOrders = Array.isArray(response.data?.data) ? response.data.data : [];
      return orders.concat(pageOrders);
    }, firstPage);
  } catch (error) {
    handleAxiosError(error, 'Failed to get orders');
  }
};

export const getOrderById = async (orderId) => {
  try {
    const res = await api.get(`/orders/${orderId}`);

    return res.data?.data || res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to get order');
  }
};

export const getOrderTracking = async (orderId) => {
  try {
    const res = await api.get(`/orders/${orderId}/tracking`);
    return res.data?.data || res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to get rider details');
  }
};

export const cancelOrder = async (orderId) => {
  try {
    const res = await api.patch(`/orders/${orderId}/cancel`);

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to cancel order');
  }
};

export const getOrderStatusHistory = async (orderId) => {
  try {
    const res = await api.get(`/orders/${orderId}/status-history`);

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to fetch order status history');
  }
};

export const confirmDeliveryByCustomer = async (orderId) => {
  try {
    const res = await api.patch(`/users/orders/${orderId}/confirm-delivery`);

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to confirm delivery');
  }
};

// ===== PAYMENTS =====
export const confirmPayment = async (orderId, paymentData = {}) => {
  try {
    const res = await api.post(
      `/orders/${orderId}/confirm-payment`,
      paymentData,
    );

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to confirm payment');
  }
};

export const getWallet = async () => {
  const res = await api.get('/wallet');
  return res.data?.data;
};

export const createWalletDeposit = async (amount, urls = {}) => {
  const res = await api.post('/wallet/deposits', {amount, ...urls});
  return res.data?.data;
};

export const confirmWalletDeposit = async depositId => {
  const res = await api.post(`/wallet/deposits/${depositId}/confirm`);
  return res.data?.data;
};

export const confirmTipPayment = async (orderId, paymentData = {}) => {
  try {
    const res = await api.post(
      `/orders/${orderId}/confirm-tip-payment`,
      paymentData,
    );

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to confirm tip payment');
  }
};

export const addTip = async (orderId, tipAmount) => {
  try {
    const res = await api.post(`/orders/${orderId}/add-tip`, {
      tipAmount,
    });

    return res.data;
  } catch (error) {
    handleAxiosError(error, 'Failed to initiate tip payment');
  }
};

// ===== ERROR HANDLER =====
const handleAxiosError = (error, defaultMsg = 'Something went wrong') => {
  if (error.response) {
    const data = error.response.data;
    let errorMessage = defaultMsg;

    if (data?.message) {
      errorMessage = data.message;
    } else if (Array.isArray(data?.errors) && data.errors.length > 0) {
      errorMessage = data.errors[0].msg || data.errors[0].message || defaultMsg;
    }

    console.error('Server responded with error:', data);
    throw new Error(errorMessage);
  }

  if (error.request) {
    console.error('No response from server:', error.request);
    throw new Error('No response from server');
  }

  console.error('Unexpected error:', error.message);
  throw new Error('Unexpected error: ' + error.message);
};

export default api;
